import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  fetchAccount,
  getStoredAuthToken,
  setStoredAuthToken,
} from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => getStoredAuthToken());
  const [user, setUser] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(Boolean(getStoredAuthToken()));

  useEffect(() => {
    let cancelled = false;

    const loadAccount = async () => {
      if (!token) {
        setUser(null);
        setAccess(null);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const account = await fetchAccount();

        if (!cancelled) {
          setUser(account.user || null);
          setAccess(account.access || null);
        }
      } catch (error) {
        if (!cancelled) {
          setStoredAuthToken(null);
          setToken(null);
          setUser(null);
          setAccess(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAccount();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const value = useMemo(() => ({
    token,
    user,
    access,
    loading,
    isAuthenticated: Boolean(token && user),
    canAccessExclusiveContent: Boolean(access?.can_access_exclusive_content),
    setAuthToken: (nextToken) => {
      setStoredAuthToken(nextToken || null);
      setToken(nextToken || null);
    },
    clearAuth: () => {
      setStoredAuthToken(null);
      setToken(null);
      setUser(null);
      setAccess(null);
    },
    refreshAccount: async () => {
      const account = await fetchAccount();
      setUser(account.user || null);
      setAccess(account.access || null);
      return account;
    },
  }), [token, user, access, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
